import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  CardPage,
  Generator,
  getTranslatedText,
  SaveCard,
  TableContainer,
  toast,
  AppContext,
  ALL_TABLES,
  getTableRowCount,
  saveTableRowCount,
  getAllColumnWidth,
  getColumnWidth as superTableColumnWidth,
  saveColumnWidth,
  getPersonalizedSorting,
  useActivityTracker
} from '@nile/components';
import { RMMClient } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import DateCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/date-cell';
import DateTimeCell from '@nile/rmm/src/modules/alert-management/components/sub-pages/date-time-cell';
import {
  ASSOCIATE_NON_MONITORED_ASSET,
  GET_ENTITY_META,
  GET_NON_MONITORED_ASSET_META_INFO_LIST_V3
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { TextCell } from '@nile/rmm/src/modules/scripts/components/cells';
import { Page } from '@superops/ui-design-system';
import gql from 'graphql-tag';
import { map } from 'lodash';
import React, { useEffect, useState, useContext } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import RMMTable from '@nile/rmm/src/modules/common/components/rmm-table/RMMTable';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CustomSecureTextCell } from '@nile/mangifera/src/Components/Ticket';
import MaskedCell from '../../../../alert-management/components/sub-pages/masked-cell';
import { ASSETS_LIST_NUM } from '@nile/rmm/src/constants';

function AssetAssociationSheet(props) {
  const [assetList, setAssetList] = useState([]);
  const flags = useFlags();
  const itDocListRef = React.useRef(null);
  itDocListRef.current = assetList;

  const { name, classId, entityName } = props.selectedNMAsset;
  const { intl } = props;
  const [assetId, setAssetId] = useState(null);

  const [entityMeta, setEntityMeta] = useState(null);

  const [allColumns, setAllColumns] = useState([]);

  const [loading, setLoading] = useState(true);

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'Name', columns: [] }
  ]);

  const [listInfo, setListInfo] = useState({});
  const appContext = useContext(AppContext);
  const { user } = appContext;
  const tableName = ALL_TABLES.associateItDocumentation;
  const columnWidthObject = getAllColumnWidth(tableName);
  const personalizedSorting = getPersonalizedSorting(tableName, entityName);
  const ROW_COUNT = getTableRowCount(tableName);
  const [rowCount, setRowCount] = useState(ROW_COUNT);
  const [records, setRecords] = useState([]);

  /**
   * formates column with custom values populated
   * uiDataType defines cell type ie 'STRING', 'PASSWORD' etc.
   */

  const getProcessedColumns = () => {
    let processedColumns = [];
    allColumns.forEach((column) => {
      entityMeta.attributes.forEach((attribute) => {
        if (column === attribute.name) {
          processedColumns.push({
            name: attribute.name,
            label: attribute.label,
            isCustom: attribute.isCustom,
            uiDataType: attribute.uiDataType
          });
        }
      });
    });
    return processedColumns;
  };

  const getFields = () => {
    const fields = getProcessedColumns().map((el) => el.name);
    let fieldsToRequest = [
      {
        listInfo: [
          'page',
          'pageSize',
          'hasMore',
          'totalCount',
          'sort { attribute order }'
        ]
      }
    ];
    const allFields = [];
    allFields.push('assetId');
    allFields.push('name');
    allFields.push(`customFields(
      fields: ${JSON.stringify(fields)}
    )`);
    fieldsToRequest.push({
      assets: allFields
    });

    return fieldsToRequest;
  };

  const getVariables = () => {
    const variables = {
      assetId: {
        type: 'ID',
        required: true
      },
      entityToAssociate: {
        type: 'String',
        required: true
      },
      listInfo: {
        pageSize: rowCount,
        page: 1,
        type: 'ListInfoInput',
        required: true,
        sort: personalizedSorting
      }
    };
    return variables;
  };

  const back = () => {
    props.toggleSheet();
  };

  const actions = {
    back: back
  };

  const context = {
    setRecords: setRecords
  };

  const columnsConfig = {
    TEXT: TextCell,
    DATE: DateCell,
    DATETIME: DateTimeCell,
    MASKED: MaskedCell,
    SecureText: CustomSecureTextCell
  };

  const getAccessor = (col) => {
    switch (col.name) {
      case 'name':
        return 'name';
      default:
        return `customFields.${col.name}`;
    }
  };

  const getType = (col) => {
    if (col.uiDataType === 'password') return 'MASKED';
    switch (col.uiDataType) {
      case 'secure_text':
        return 'SecureText';
      default:
    }
    if (col.name.includes('date')) {
      return 'DATE';
    }
    if (col.name.includes('dt')) {
      return 'DATETIME';
    }
    switch (col.name) {
      default:
        return 'TEXT';
    }
  };

  const getColumnWidth = (colName, isMinWidth = false) => {
    switch (colName) {
      case 'platform':
        return 300;
      default:
        return isMinWidth ? 50 : 180;
    }
  };

  const formatLabel = (label) => {
    if (label.includes('attribute')) return label?.split('.')[1];
    return label;
  };

  const constructTableData = (cols, data) => {
    const columns = cols.map((col, idx) => {
      return {
        header: getTranslatedText(intl, col.label),
        accessorKey: getAccessor(col),
        size: superTableColumnWidth(
          columnWidthObject,
          col.name,
          getColumnWidth(col.name)
        ),
        minSize: getColumnWidth(col.name),
        Header: getTranslatedText(intl, col.label),
        minWidth: getColumnWidth(col.name, true),
        width: getColumnWidth(col.name),
        accessor: getAccessor(col),
        type: getType(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableFilters: true,
        editable: false,
        module: entityName,
        field: { ...col, label: formatLabel(col?.label) }
      };
    });
    setColumnGroup([{ Header: 'Name', header: 'Name', columns }]);
    setAssetList(data?.assets);
  };

  const fetchAssetList = (pageOptions) => {
    const processedCustomFields = getProcessedColumns().map((el) => el.name);
    const generator = new Generator({
      operation: 'getNonMonitoredAssetListToAssociate',
      variables: getVariables(),
      fields: getFields()
    });
    let sortData = pageOptions?.sort || personalizedSorting || null;
    if (!processedCustomFields.includes(sortData?.[0]?.attribute)) {
      sortData = pageOptions?.sort || null;
    }
    const queryString = generator.queryBuilder();
    setLoading(true);
    RMMClient.query({
      query: gql`
        ${queryString.query}
      `,
      variables: {
        assetId,
        entityToAssociate: entityName,
        listInfo: pageOptions
          ? {
              ...pageOptions,
              sort: sortData,
              condition: props.defaultFilter // TO DO: If inline/advanced filters are implemented append filter conditions to default conditions
            }
          : {
              pageSize: rowCount,
              page: 1,
              sort: sortData,
              condition: props.defaultFilter // TO DO: If inline/advanced filters are implemented append filter conditions to default conditions
            }
      },
      fetchPolicy: 'network-only'
    })
      .then((response) => {
        setLoading(false);
        const list = response?.data?.getNonMonitoredAssetListToAssociate;
        const pgOptions = pageOptions || {};
        if (list) {
          constructTableData(getProcessedColumns(), list);
          setListInfo({ ...list.listInfo, ...pgOptions });
        }
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (allColumns.length > 0 && entityMeta?.attributes) {
      fetchAssetList();
    }
  }, [allColumns, entityMeta]);

  useEffect(() => {
    const { assetId } = props.match.params;
    if (assetId) {
      setAssetId(assetId);
    }
  }, [props.match.params.assetId]);

  useEffect(() => {
    const { id } = props.match.params;
    if (id) {
      setAssetId(id);
    }
  }, [props.match.params.id]);

  const [fetchAssetMetaInfo] = useLazyQuery(
    GET_NON_MONITORED_ASSET_META_INFO_LIST_V3,
    {
      client: RMMClient,
      fetchPolicy: 'network-only',
      onCompleted({ getNonMonitoredAssetMetaInfoV3 }) {
        setAllColumns(getNonMonitoredAssetMetaInfoV3.selectAttributes);
      }
    }
  );

  useQuery(GET_ENTITY_META, {
    client: RMMClient,
    variables: {
      entityName
    },
    onCompleted: (res) => {
      setEntityMeta(res?.getEntityMeta);
      fetchAssetMetaInfo({
        variables: {
          classId
        }
      });
    }
  });

  const [
    associateSelectedAssets,
    { loading: associateSaveLoading }
  ] = useMutation(ASSOCIATE_NON_MONITORED_ASSET, {
    client: RMMClient,
    onCompleted() {
      toast('success', 'Asset associated successfully');
      props.refetchAsset({
        name,
        entityName
      });
    },
    onError() {
      // toast('error', 'Something went wrong');
    }
  });

  const associateAssets = () => {
    let classId = props?.match?.params.classId;
    useActivityTracker({
      event: `Associating IT Documentation to ${ASSETS_LIST_NUM[classId]} device`
    });
    if (records.length)
      associateSelectedAssets({
        variables: {
          assetId,
          relatedAsset: {
            entityName,
            assetIds: map(records, 'assetId')
          }
        }
      });
  };

  const tableChange = (personalization, pageOptions) => {
    if (pageOptions) setListInfo({ ...listInfo, ...pageOptions });
    if (!allColumns || !entityMeta?.attributes) return;
    fetchAssetList(pageOptions);
  };

  return (
    <CardPage isNewLayout fullHeight tableContent className="vw50 full-height">
      <Page
        variant="secondary"
        contentHeight="80px"
        title={name}
        primaryAction={
          <SaveCard
            removeSecondaryBtnBg
            actions={actions}
            handleSave={associateAssets}
            loading={associateSaveLoading}
          />
        }
      >
        <TableContainer>
          <RMMTable
            cardTable
            fullHeight
            isLoading={loading}
            context={context}
            columns={columnGroup}
            data={itDocListRef.current}
            rowCount={rowCount}
            refreshTable={tableChange}
            listInfo={listInfo}
            defaultColumnCells={columnsConfig}
            pagination={
              Math.ceil(listInfo?.totalCount / listInfo?.pageSize) > 1
            }
            pages={Math.ceil(listInfo?.totalCount / listInfo?.pageSize) || 0}
            recordCount={listInfo?.totalCount || 0}
            activePage={listInfo?.page || 1}
            emptyState={{
              nodataImage: `${envConfig.imageRoot}svg/not-found.svg`,
              description: getTranslatedText(
                intl,
                'it.document.no.associate.assets.desc'
              )
            }}
            ldFlagEnabled={flags?.associateItDocumentation}
            listInfoDetails={listInfo}
            setRowCount={setRowCount}
            saveTableRowCount={saveTableRowCount}
            user={user}
            saveColumnWidth={saveColumnWidth}
            tableName={tableName}
            columnWidthPersonalization
            padded="lg"
            checkBoxSize="lg"
            sortingPersonalization
            viewId={entityName}
          />
        </TableContainer>
      </Page>
    </CardPage>
  );
}

export default injectIntl(withRouter(AssetAssociationSheet));
